import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { USER_URL } from '@insite-store360/shared';
import type { LayoutConfig, StoreUIMenu } from '@insite-store360/shared';
import { ADMIN_ROLE_ID } from 'library/api-common/src/lib/utils/appliationConstants';
import { Observable, catchError, firstValueFrom, map, of } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface MenuSettingNode {
  id: number;
  parentId: number | null;
  projectId: number;
  title: string;
  matIcon: string | null;
  routerLink: string | null;
  routerLinkActiveExact: boolean;
  permission: string | null;
  position: number;
  isActive: boolean;
  countryIds: number[] | null;
  visible?: boolean;
  isVisible?: boolean;
  subMenu?: MenuSettingNode[];
}

export interface ResolvedMenuResponse {
  useStatic: boolean;
  menu: StoreUIMenu[];
}

export const MENU_SETTING_ROUTE = '/admin/user/menu-setting';
const QUIZ_MANDATORY_ROUTE = '/admin/activity-calendar/setup/quiz-mandatory';

@Injectable({
  providedIn: 'root',
})
export class MenuSettingService {
  private readonly api = `${USER_URL}/menu-setting`;

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandler: ErrorHandlerService,
  ) {}

  getTree(projectId = 1): Observable<MenuSettingNode[]> {
    return this.http
      .get<unknown>(`${this.api}/tree`, {
        params: new HttpParams().set('projectId', String(projectId)),
      })
      .pipe(
        map((res) => this.unwrap<MenuSettingNode[]>(res)),
        catchError(this.errorHandler.handleError),
      );
  }

  getResolved(params: {
    roleId: number;
    userId?: number;
    countryIds?: number[];
    projectId?: number;
  }): Observable<ResolvedMenuResponse> {
    let httpParams = new HttpParams()
      .set('roleId', String(params.roleId))
      .set('projectId', String(params.projectId ?? 1));
    if (params.userId) {
      httpParams = httpParams.set('userId', String(params.userId));
    }
    if (params.countryIds?.length) {
      httpParams = httpParams.set('countryIds', params.countryIds.join(','));
    }
    return this.http.get<unknown>(`${this.api}/resolved`, { params: httpParams }).pipe(
      map((res) => this.unwrap<ResolvedMenuResponse>(res)),
      catchError((err) => {
        console.error('[MenuSetting] /resolved failed — falling back to static layout', {
          roleId: params.roleId,
          projectId: params.projectId,
          err,
        });
        return of({ useStatic: true, menu: [] });
      }),
    );
  }

  getAccess(params: {
    roleId: number;
    userId?: number;
    countryId?: number;
    projectId?: number;
  }): Observable<MenuSettingNode[]> {
    let httpParams = new HttpParams()
      .set('roleId', String(params.roleId))
      .set('projectId', String(params.projectId ?? 1));
    if (params.userId) {
      httpParams = httpParams.set('userId', String(params.userId));
    }
    if (params.countryId) {
      httpParams = httpParams.set('countryId', String(params.countryId));
    }
    return this.http.get<unknown>(`${this.api}/access`, { params: httpParams }).pipe(
      map((res) => this.unwrap<MenuSettingNode[]>(res)),
      catchError(this.errorHandler.handleError),
    );
  }

  getUsers(roleId: number, countryId?: number): Observable<any[]> {
    let httpParams = new HttpParams().set('roleId', String(roleId));
    if (countryId) {
      httpParams = httpParams.set('countryId', String(countryId));
    }
    return this.http.get<unknown>(`${this.api}/users`, { params: httpParams }).pipe(
      map((res) => this.unwrap<any[]>(res)),
      catchError(this.errorHandler.handleError),
    );
  }

  saveItem(payload: Partial<MenuSettingNode>): Observable<MenuSettingNode> {
    return this.http
      .post<unknown>(`${this.api}/save`, payload)
      .pipe(
        map((res) => this.unwrap<MenuSettingNode>(res)),
        catchError(this.errorHandler.handleError),
      );
  }

  deleteItem(id: number): Observable<unknown> {
    return this.http
      .delete<unknown>(`${this.api}/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  moveItem(id: number, direction: 'up' | 'down'): Observable<MenuSettingNode[]> {
    return this.http
      .post<unknown>(`${this.api}/move/${id}/${direction}`, {})
      .pipe(
        map((res) => this.unwrap<MenuSettingNode[]>(res)),
        catchError(this.errorHandler.handleError),
      );
  }

  saveAccess(payload: {
    roleId: number;
    userId?: number | null;
    countryId?: number | null;
    projectId?: number;
    items: Array<{ menuItemId: number; isVisible: boolean }>;
  }): Observable<unknown> {
    return this.http
      .post<unknown>(`${this.api}/access`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  clearUserOverride(userId: number, projectId = 1): Observable<{ deleted: number }> {
    return this.http
      .delete<unknown>(`${this.api}/access/user/${userId}`, {
        params: new HttpParams().set('projectId', String(projectId)),
      })
      .pipe(
        map((res) => this.unwrap<{ deleted: number }>(res)),
        catchError(this.errorHandler.handleError),
      );
  }

  importLayout(payload: {
    roleId: number;
    projectId?: number;
    assignToRole?: boolean;
    menu: StoreUIMenu[];
  }): Observable<{ created: number; updated: number; assigned: number }> {
    return this.http
      .post<unknown>(`${this.api}/import`, payload)
      .pipe(
        map((res) => this.unwrap<{ created: number; updated: number; assigned: number }>(res)),
        catchError(this.errorHandler.handleError),
      );
  }

  async resolveLayoutConfig(params: {
    roleId: number;
    userId?: number;
    countryIds?: number[];
    projectId: number;
    fallback: LayoutConfig;
  }): Promise<LayoutConfig> {
    const load = (userId?: number) =>
      firstValueFrom(
        this.getResolved({
          roleId: params.roleId,
          userId,
          countryIds: params.countryIds,
          projectId: params.projectId,
        }),
      );

    let resolved = await load(params.userId);

    // If a stale user override hides everything / forces static, fall back to
    // role-level Menu Setting so Admin Assign Access for the role still works.
    if (
      params.userId &&
      (resolved?.useStatic || !resolved?.menu?.length)
    ) {
      const roleOnly = await load(undefined);
      if (!roleOnly?.useStatic && roleOnly?.menu?.length) {
        console.warn(
          '[MenuSetting] Ignoring empty/static user override; using role menu',
          { roleId: params.roleId, userId: params.userId },
        );
        resolved = roleOnly;
      }
    }

    const fallbackMenu = params.fallback?.sideNavConfig?.menu ?? [];

    console.info('[MenuSetting] resolveLayoutConfig', {
      roleId: params.roleId,
      projectId: params.projectId,
      useStatic: resolved?.useStatic,
      menuCount: resolved?.menu?.length ?? 0,
    });

    // useStatic:true only when the role has no Menu Setting config yet.
    // If Menu Setting is configured, always use the API menu — even when sparse —
    // so Assign Access is never replaced by the full hardcoded role layout.
    if (resolved?.useStatic) {
      const menu = this.ensureTrainingDashboardMenu(
        this.mergeProjectSwitchItems(
          this.applyQuizMandatoryMenu(fallbackMenu, params.roleId, params.projectId),
          resolved?.menu as StoreUIMenu[] | undefined,
          params.projectId,
        ),
      );
      if (menu === fallbackMenu) {
        return params.fallback;
      }
      return {
        ...params.fallback,
        sideNavConfig: {
          ...params.fallback.sideNavConfig,
          menu,
        },
      };
    }

    const menu = this.ensureTrainingDashboardMenu(
      this.applyQuizMandatoryMenu(
        this.ensureAdminMenuSetting(
          this.pruneEmptySubMenus((resolved?.menu ?? []) as StoreUIMenu[]),
          params.roleId,
          params.projectId,
        ),
        params.roleId,
        params.projectId,
      ),
    );
    return {
      ...params.fallback,
      sideNavConfig: {
        ...params.fallback.sideNavConfig,
        menu,
      },
    };
  }

  hasNestedMenu(item: StoreUIMenu | undefined | null): boolean {
    return Array.isArray(item?.subMenu) && item.subMenu.some((child) => child.visible !== false);
  }

  private pruneEmptySubMenus(menu: StoreUIMenu[]): StoreUIMenu[] {
    return menu
      .filter((item) => item.visible !== false)
      .map((item) => {
        const children = item.subMenu?.length ? this.pruneEmptySubMenus(item.subMenu) : [];
        if (!children.length) {
          const { subMenu: _subMenu, ...rest } = item;
          return rest;
        }
        return { ...item, subMenu: children };
      });
  }

  ensureAdminMenuSetting(
    menu: StoreUIMenu[],
    roleId: number,
    projectId: number,
  ): StoreUIMenu[] {
    if (Number(roleId) !== ADMIN_ROLE_ID || Number(projectId) !== 1) {
      return menu;
    }
    if (this.menuContainsRoute(menu, MENU_SETTING_ROUTE)) {
      return menu;
    }
    const cloned = JSON.parse(JSON.stringify(menu)) as StoreUIMenu[];
    const settingItem: StoreUIMenu = {
      title: 'Menu Setting',
      matIcon: 'menu',
      routerLink: MENU_SETTING_ROUTE,
      routerLinkActiveExact: true,
      visible: true,
    };
    const userMenu = cloned.find((item) => item.title === 'User' || item.title === 'Admin');
    if (userMenu) {
      userMenu.subMenu = [...(userMenu.subMenu ?? []), settingItem];
      return cloned;
    }
    return [...cloned, settingItem];
  }

  /** Keep Menu Setting project-switch rows when the rest of the menu falls back to a static layout. */
  private mergeProjectSwitchItems(
    menu: StoreUIMenu[],
    resolved: StoreUIMenu[] | undefined,
    projectId: number,
  ): StoreUIMenu[] {
    if (Number(projectId) !== 1 || !resolved?.length) {
      return menu;
    }
    const switches: StoreUIMenu[] = [];
    const walk = (items: StoreUIMenu[] | undefined) => {
      for (const item of items || []) {
        if (
          item.visible !== false &&
          typeof item.routerLink === 'string' &&
          item.routerLink.startsWith('switch-project:')
        ) {
          const { subMenu: _subMenu, ...leaf } = item;
          switches.push(leaf);
        }
        if (item.subMenu?.length) {
          walk(item.subMenu);
        }
      }
    };
    walk(resolved);
    if (!switches.length) {
      return menu;
    }
    const existing = new Set<string>();
    const mark = (items: StoreUIMenu[]) => {
      for (const item of items) {
        if (item.routerLink) {
          existing.add(item.routerLink);
        }
        if (item.subMenu?.length) {
          mark(item.subMenu);
        }
      }
    };
    mark(menu);
    const extra = switches.filter((item) => item.routerLink && !existing.has(item.routerLink));
    if (!extra.length) {
      return menu;
    }
    return [...menu, ...extra];
  }

  private menuContainsRoute(menu: StoreUIMenu[], route: string): boolean {
    for (const item of menu) {
      if (item.routerLink === route) {
        return true;
      }
      if (item.subMenu && this.menuContainsRoute(item.subMenu, route)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Ensure Training dashboard sits on the ops Training menu.
   * Allowed homes:
   * - Trade Operations → Training (Admin / BA layouts)
   * - Top-level Training (Training Manager / Trainer layouts)
   * Never under Setups → Training (type/module setup), which used to steal top-nav highlight.
   */
  private ensureTrainingDashboardMenu(menu: StoreUIMenu[]): StoreUIMenu[] {
    const route = '/admin/training/training-dashboard';
    const cloned = JSON.parse(JSON.stringify(menu)) as StoreUIMenu[];
    this.pruneMisplacedTrainingDashboard(cloned, route);
    if (!this.menuContainsRoute(cloned, route)) {
      this.addTrainingDashboardItem(cloned, route);
    }
    return cloned;
  }

  /** True when this Training folder is the right place for the dashboard leaf. */
  private isOpsTrainingDashboardHome(trainingItem: StoreUIMenu, chain: StoreUIMenu[]): boolean {
    if (trainingItem.title !== 'Training') {
      return false;
    }
    if (chain.some((node) => node.title === 'Setups' || node.title === 'Setup')) {
      return false;
    }
    if (chain.some((node) => node.title === 'Trade Operations')) {
      return true;
    }
    // Top-level Training menu (e.g. CA Trainer / Training Manager)
    return chain.length === 1;
  }

  private pruneMisplacedTrainingDashboard(
    menu: StoreUIMenu[],
    route: string,
    ancestors: StoreUIMenu[] = [],
  ): void {
    for (const item of menu) {
      const chain = [...ancestors, item];
      if (!item.subMenu?.length) {
        continue;
      }
      const keepHere = this.isOpsTrainingDashboardHome(item, chain);
      item.subMenu = item.subMenu.filter((child) => {
        const link = (child.routerLink || '').split('?')[0];
        if (link !== route) {
          return true;
        }
        return keepHere;
      });
      this.pruneMisplacedTrainingDashboard(item.subMenu, route, chain);
    }
  }

  private addTrainingDashboardItem(
    menu: StoreUIMenu[],
    route: string,
    ancestors: StoreUIMenu[] = [],
  ): boolean {
    // Prefer Trade Ops → Training when both exist (Admin), else top-level Training.
    const tradeOpsHit = this.addTrainingDashboardUnderPreferredHome(menu, route, ancestors, true);
    if (tradeOpsHit) {
      return true;
    }
    return this.addTrainingDashboardUnderPreferredHome(menu, route, ancestors, false);
  }

  private addTrainingDashboardUnderPreferredHome(
    menu: StoreUIMenu[],
    route: string,
    ancestors: StoreUIMenu[],
    requireTradeOps: boolean,
  ): boolean {
    for (const item of menu) {
      const chain = [...ancestors, item];
      const underTradeOps = chain.some((node) => node.title === 'Trade Operations');
      if (
        item.subMenu &&
        this.isOpsTrainingDashboardHome(item, chain) &&
        (!requireTradeOps || underTradeOps)
      ) {
        item.subMenu.splice(
          Math.max(
            item.subMenu.findIndex(
              (child) => child.routerLink === '/admin/training/training-summary',
            ) + 1,
            0,
          ),
          0,
          {
            title: 'Training dashboard',
            matIcon: 'store',
            routerLink: route,
            routerLinkActiveExact: true,
            visible: true,
          },
        );
        return true;
      }
      if (
        item.subMenu?.length &&
        this.addTrainingDashboardUnderPreferredHome(item.subMenu, route, chain, requireTradeOps)
      ) {
        return true;
      }
    }
    return false;
  }

  private applyQuizMandatoryMenu(
    menu: StoreUIMenu[],
    roleId: number,
    projectId: number,
  ): StoreUIMenu[] {
    if (Number(projectId) !== 1) {
      return menu;
    }
    if (Number(roleId) === ADMIN_ROLE_ID) {
      return this.ensureQuizMandatoryMenu(menu);
    }
    return menu;
  }

  private ensureQuizMandatoryMenu(menu: StoreUIMenu[]): StoreUIMenu[] {
    if (this.menuContainsRoute(menu, QUIZ_MANDATORY_ROUTE)) {
      return menu;
    }
    const cloned = JSON.parse(JSON.stringify(menu)) as StoreUIMenu[];
    const generalSettings = this.findTradeCalendarGeneralSettings(cloned);
    if (!generalSettings) {
      return menu;
    }
    generalSettings.subMenu = [
      ...(generalSettings.subMenu ?? []),
      {
        title: 'Quiz Mandatory',
        matIcon: 'store',
        routerLink: QUIZ_MANDATORY_ROUTE,
        routerLinkActiveExact: true,
        visible: true,
      },
    ];
    return cloned;
  }

  private findTradeCalendarGeneralSettings(menu: StoreUIMenu[]): StoreUIMenu | undefined {
    for (const item of menu) {
      if (
        item.title === 'General Settings' &&
        item.subMenu?.some(
          (child) =>
            child.routerLink === '/admin/activity-calendar/setup/launch-project' ||
            child.routerLink === '/admin/activity-calendar/setup/supporting-teams' ||
            child.routerLink === '/admin/activity-calendar/setup/status',
        )
      ) {
        return item;
      }
      if (item.subMenu?.length) {
        const found = this.findTradeCalendarGeneralSettings(item.subMenu);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  private unwrap<T>(res: unknown): T {
    if (res && typeof res === 'object' && 'result' in res) {
      return (res as { result: T }).result;
    }
    return res as T;
  }
}
